// Contact schema definitions for the universal system
import { z } from "zod";
import { CONTACT_STATUS_VALUES } from "@/lib/constants/contact-statuses";

// Contact status and type enums
export const ContactStatusSchema = z.enum(CONTACT_STATUS_VALUES);

const CONTACT_STATUS_LABELS: Record<(typeof CONTACT_STATUS_VALUES)[number], string> = {
  lead: "Lead",
  prospect: "Prospect",
  customer: "Customer",
  churned: "Churned",
  inactive: "Inactive",
  personal: "Personal",
  system_notification: "System/Notification",
  other_non_revenue: "Other (Non-Revenue)",
};

/** UI select options — values from `CONTACT_STATUS_VALUES`. */
export const CONTACT_STATUS_OPTIONS = CONTACT_STATUS_VALUES.map((value) => ({
  label: CONTACT_STATUS_LABELS[value],
  value,
}));

export const CONTACT_INDUSTRY_OPTIONS = [
  { label: "Technology", value: "technology" },
  { label: "Healthcare", value: "healthcare" },
  { label: "Finance", value: "finance" },
  { label: "Education", value: "education" },
  { label: "Manufacturing", value: "manufacturing" },
  { label: "Retail", value: "retail" },
  { label: "Real Estate", value: "real_estate" },
  { label: "Legal", value: "legal" },
  { label: "Consulting", value: "consulting" },
  { label: "Non-Profit", value: "non_profit" },
  { label: "Government", value: "government" },
  { label: "Other", value: "other" },
];

export const CONTACT_SOURCE_OPTIONS = [
  { label: "Website", value: "website" },
  { label: "Referral", value: "referral" },
  { label: "Cold Call", value: "cold_call" },
  { label: "Email Campaign", value: "email_campaign" },
  { label: "Social Media", value: "social_media" },
  { label: "Trade Show", value: "trade_show" },
  { label: "Partner", value: "partner" },
  { label: "Advertisement", value: "advertisement" },
  { label: "Other", value: "other" },
];

export const CONTACT_CONTRACT_TYPE_OPTIONS = [
  { label: "Hourly", value: "hourly" },
  { label: "Monthly Retainer", value: "monthly_retainer" },
  { label: "Project Based", value: "project_based" },
  { label: "Fixed Price", value: "fixed_price" },
  { label: "Not Applicable", value: "not_applicable" },
];

// Webhook schemas for contact webhooks
export const ContactWebhookSchema = z.object({
  name: z.string().min(1, "Contact name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().optional(),
  company_name: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip_code: z.string().optional(),
  country: z.string().optional(),
  website: z.string().optional(),
  industry: z.string().optional(),
  title: z.string().optional(),
  status: ContactStatusSchema.optional(),
  tags: z.array(z.string()).optional(),
  lifecycle_stage: z.string().optional(),
  source: z.string().optional(),
  avatar_url: z.string().optional(),
  social_links: z.record(z.string(), z.any()).optional(),
  custom_fields: z.record(z.string(), z.any()).optional(),
  is_active: z.boolean().optional(),
  contract_type: z.string().optional(),
  hourly_rate: z.number().optional(),
  monthly_rate: z.number().optional(),
  payment_terms: z.string().optional(),
  tax_id: z.string().optional(),
  notes: z.string().optional(),
  createdByEmail: z.string().email("Valid creator email is required"),
});

export type ContactWebhookPayload = z.infer<typeof ContactWebhookSchema>;

// ============================================================================
// EDIT CLIENT FORM SCHEMA - Single Source of Truth
// ============================================================================
// Used for client/contact editing with full validation and type safety.
// Includes all fields from the edit form sections: Contact, Address, Contract, Notes, Status.

export const EditClientFormSchema = z.object({
  // Contact Information Section
  name: z.string().min(2, "Name must be at least 2 characters").max(255),
  company_name: z.string().max(255).optional().or(z.literal("")),
  email: z.string().email("Valid email required").min(1, "Email is required"),
  phone: z.string().max(50).optional().or(z.literal("")),
  website: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  industry: z.string().max(100).optional().or(z.literal("")),

  // Address Information Section
  address: z.string().max(500).optional().or(z.literal("")),
  city: z.string().max(100).optional().or(z.literal("")),
  state: z.string().max(100).optional().or(z.literal("")),
  zip_code: z.string().max(20).optional().or(z.literal("")),
  country: z.string().max(100).optional().or(z.literal("")),

  // Contract & Financial Section
  contract_type: z.string().optional(),
  hourly_rate: z.number().min(0, "Rate must be positive").optional(),
  monthly_rate: z.number().min(0, "Rate must be positive").optional(),
  payment_terms: z.string().max(255).optional().or(z.literal("")),
  tax_id: z.string().max(50).optional().or(z.literal("")),

  // Notes Section
  notes: z.string().optional().or(z.literal("")),

  // Status Section
  is_active: z.boolean().default(true),
});

export type EditClientForm = z.infer<typeof EditClientFormSchema>;
